import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GridOnIcon from "@mui/icons-material/GridOn";
import DescriptionIcon from "@mui/icons-material/Description";

import {
  previewPqrsdReport,
  downloadPqrsdReportXlsx,
  downloadPqrsdReportCsv,
} from "../../services/backend/PqrsdService";

import { pqrsdText } from "./pqrsdLabel";
// The Excel reports of Informes PQRSD. Each one is built by pqrsdStats (action
// REPORT); the period is the page's date range.
export const PQRSD_REPORTS = [
  {
    key: "reopened",
    label: "Reaperturas",
    file: "reaperturas",
    empty: "Ningún ciudadano reabrió una PQRSD en este período.",
    description:
      "Cada vez que un ciudadano reabrió una PQRSD después de la respuesta final: cuándo, con qué "
      + "motivo, lo que explicó y cómo calificó al final. Se filtra por la fecha de la reapertura.",
  },
  {
    key: "ratings",
    label: "Calificaciones",
    file: "calificaciones",
    empty: "Ningún ciudadano calificó una respuesta en este período.",
    description:
      "Cada PQRSD que el ciudadano calificó en la encuesta de satisfacción: la opción que eligió, su "
      + "puntaje de 1 a 5 y cuántas veces la había reabierto. Se filtra por la fecha de la calificación.",
  },
];

// Rows rendered in the preview; the files always carry all of them.
const PREVIEW_LIMIT = 200;

// Preview column sizing by kind: codes, dates and counts stay on one line;
// prose gets room and wraps; everything else gets a sane minimum. The table
// scrolls sideways instead of crushing columns letter by letter.
const NOWRAP = new Set(["radicado", "filedAt", "reopenedAt", "reopenNumber", "status", "ratedAt", "score"]);
const WIDE = new Set(["subject", "detail", "reason"]);
const cellSx = (key) => (NOWRAP.has(key)
  ? { whiteSpace: "nowrap" }
  : { minWidth: WIDE.has(key) ? 240 : 150, maxWidth: WIDE.has(key) ? 420 : 260, whiteSpace: "pre-wrap", wordBreak: "break-word" });

const errorText = (code, what) =>
  code === "FORBIDDEN"
    ? pqrsdText("No tiene permiso para ver los informes de PQRSD (pqrsd.reports).")
    : `No fue posible generar ${what}. Intente de nuevo.`;

export default function PqrsdReportBuilder({ from, to }) {
  const [reportKey, setReportKey] = useState(PQRSD_REPORTS[0].key);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null); // "preview" | "xlsx" | "csv"
  const [notice, setNotice] = useState(null);

  const report = PQRSD_REPORTS.find((r) => r.key === reportKey) || PQRSD_REPORTS[0];
  const filters = () => ({
    report: report.key,
    // moment objects mutate in place: widen copies, not the pickers' values.
    from: from ? from.clone().startOf("day").format() : null,
    to: to ? to.clone().endOf("day").format() : null,
  });
  const fileName = (ext) => `pqrsd_${report.file}_${moment().format("YYYYMMDD")}.${ext}`;

  // A preview of another report or period would be misleading: drop it.
  useEffect(() => { setPreview(null); setNotice(null); }, [reportKey, from, to]);

  const fetchRows = async () => {
    if (preview) return { body: preview, error: null };
    const out = await previewPqrsdReport(filters());
    if (out.body) setPreview(out.body);
    return out;
  };

  const runPreview = async () => {
    setBusy("preview");
    setNotice(null);
    setPreview(null);
    const { body, error } = await previewPqrsdReport(filters());
    setBusy(null);
    if (error) return setNotice({ severity: "error", text: errorText(error, "la vista previa") });
    setPreview(body);
  };

  const runCsv = async () => {
    setBusy("csv");
    setNotice(null);
    const { body, error } = await fetchRows();
    setBusy(null);
    if (error) return setNotice({ severity: "error", text: errorText(error, "el CSV") });
    downloadPqrsdReportCsv(fileName("csv"), body);
  };

  // Excel first; if the server cannot build it, the same rows as a CSV built
  // here, so the user still leaves with the data.
  const runXlsx = async () => {
    setBusy("xlsx");
    setNotice(null);
    const err = await downloadPqrsdReportXlsx(filters());
    if (!err) {
      setBusy(null);
      return;
    }
    if (err === "FORBIDDEN") {
      setBusy(null);
      return setNotice({ severity: "error", text: errorText(err, "el archivo de Excel") });
    }
    const { body, error } = await fetchRows();
    setBusy(null);
    if (error) return setNotice({ severity: "error", text: errorText(error, "el archivo de Excel ni el CSV") });
    downloadPqrsdReportCsv(fileName("csv"), body);
    setNotice({
      severity: "warning",
      text: "No fue posible generar el Excel. Se descargó un CSV con los mismos datos (se abre en Excel).",
    });
  };

  const shown = preview ? preview.rows.slice(0, PREVIEW_LIMIT) : [];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {PQRSD_REPORTS.map((r) => (
            <Chip
              key={r.key}
              label={r.label}
              color={r.key === reportKey ? "primary" : "default"}
              variant={r.key === reportKey ? "filled" : "outlined"}
              onClick={() => setReportKey(r.key)}
            />
          ))}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pqrsdText(report.description)}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            onClick={runPreview}
            disabled={!!busy}
            startIcon={busy === "preview" ? <CircularProgress size={18} /> : <VisibilityIcon />}
          >
            Vista previa
          </Button>
          <Button
            variant="contained"
            onClick={runXlsx}
            disabled={!!busy}
            startIcon={busy === "xlsx" ? <CircularProgress size={18} color="inherit" /> : <GridOnIcon />}
          >
            Descargar Excel
          </Button>
          <Button
            onClick={runCsv}
            disabled={!!busy}
            startIcon={busy === "csv" ? <CircularProgress size={18} /> : <DescriptionIcon />}
          >
            CSV
          </Button>
        </Stack>
      </Paper>

      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert>}

      {preview && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1">{preview.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {preview.period} · {preview.total} {preview.total === 1 ? "fila" : "filas"}
              {preview.total > PREVIEW_LIMIT ? ` (se muestran las primeras ${PREVIEW_LIMIT}; el archivo las trae todas)` : ""}
            </Typography>
          </Box>
          {preview.total === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
              {pqrsdText(report.empty)}
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {preview.columns.map((c) => (
                      <TableCell key={c.key} sx={{ fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "bottom" }}>{c.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((row, i) => (
                    <TableRow key={i} hover>
                      {row.map((v, j) => (
                        <TableCell key={j} sx={{ verticalAlign: "top", ...cellSx(preview.columns[j]?.key) }}>
                          {String(v ?? "")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Stack>
  );
}
